/* eslint-disable @typescript-eslint/no-unused-vars */
import { PolylineSymbol as sPolylineSymbol } from "@evergis/sgis/es/symbols/PolylineSymbol";
import { Crs } from "@evergis/sgis/es/Crs";
import { Polyline } from "@evergis/sgis/es/features/Polyline";
import { Render } from "@evergis/sgis/es/renders/Render";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { SimpleEndingDc } from "@evergis/api";

import { isDashedBrush, PolylineMiterSymbol } from "../../interfaces/style";
import { getParameterValue } from "../../evergis/core/classification/getFromClassified";
import { getLineDash } from "./SymbolRenderer/strokeStyle";
import { getHexColor } from "../../utils/color";
import { clamp } from "../../utils/math";
import { getLineMiterRender, LinerMiterRenderOptions } from "./SymbolRenderer/LineMiters/getLineMiterRender";
import { updateRingsForMiter } from "./SymbolRenderer/LineMiters/updateRingsForMiter";
import { PREVIEW_LIMITS } from "./SymbolRenderer/previewLimits";
import { MiterPosition } from "./SymbolRenderer/LineMiters/LineMiterRender";

export const copyRings = (rings: Coordinates[][]): Coordinates[][] =>
  rings.map(ring => ring.map(coordinates => coordinates.slice() as Coordinates));

export class SGisMiterSymbol extends sPolylineSymbol {
  constructor(public originalSymbol: PolylineMiterSymbol) {
    super({
      strokeColor: getHexColor(getParameterValue(originalSymbol.stroke?.color)),
      strokeWidth: clamp(
        getParameterValue(originalSymbol.stroke?.width),
        PREVIEW_LIMITS.STROKE_WIDTH_MIN,
        PREVIEW_LIMITS.STROKE_WIDTH_MAX,
      ),
      lineDash: getLineDash(originalSymbol.stroke),
      // TODO?: may be it should be defined by user
      lineCap: isDashedBrush(originalSymbol.stroke) ? "square" : "round",
    });
  }

  getMiterRender = (
    position: MiterPosition,
    feature: Polyline,
    resolution: number,
    crs: Crs,
    renderEmptyMiter?: boolean,
  ): Render | null => {
    const { beginning, ending } = this.originalSymbol;

    const [miterKind, miterSize] =
      position === "left"
        ? [(beginning as SimpleEndingDc)?.type || null, (beginning as SimpleEndingDc)?.size || null]
        : [(ending as SimpleEndingDc)?.type || null, (ending as SimpleEndingDc)?.size || null];

    const miterRender = getLineMiterRender({
      renderEmptyMiter,
      color: this.strokeColor,
      kind: miterKind,
      position,
      size: miterSize,
      strokeWidth: this.strokeWidth,
    } as LinerMiterRenderOptions);

    if (!miterRender) return null;

    const updatedRings = updateRingsForMiter(feature.rings, position, miterRender.usageSize);
    const render = miterRender.getRender(updatedRings, feature.crs, resolution, crs);

    feature.rings = updatedRings;

    return render;
  };

  getRenders(feature: Polyline, resolution: number, crs: Crs, renderEmptyMiter?: boolean): Render[] {
    return [
      this.getMiterRender("left", feature, resolution, crs, renderEmptyMiter),
      this.getMiterRender("right", feature, resolution, crs, renderEmptyMiter),
    ].filter(Boolean);
  }

  renderFunction(feature: Polyline, resolution: number, crs: Crs): Render[] {
    return this.getRenders(feature.clone(), resolution, crs, true);
  }

  clone() {
    return new SGisMiterSymbol(this.originalSymbol);
  }
}
